import { DatePicker } from './DatePicker.tsx';
import { useT } from '~/i18n/index.tsx';

/**
 * A moment: our calendar for the day, a plain field for the time.
 *
 * `<input type="datetime-local">` is really two controls behind one border, and only one of them
 * is the problem. The time half is a masked text field the browser draws in the page's own font;
 * the date half opens the OS calendar — a Chrome-drawn panel in the system font, with a US-format
 * mask and a week that starts wherever the browser decides. So the date half is replaced with
 * {@link DatePicker} and the time half is kept and styled, rather than rebuilding a clock nobody
 * asked for.
 *
 * The value is `YYYY-MM-DDTHH:mm` in and out — what the announcement API already sends and
 * receives — so nothing downstream changes.
 *
 * An empty time with a date set means midnight; a cleared date clears the whole value, because a
 * time on no particular day is not a moment.
 */
export function DateTimeField({
  id,
  value,
  onChange,
  min,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  /** An ISO datetime; only its day bounds the calendar. */
  min?: string;
}) {
  const t = useT();
  const [date = '', time = ''] = value.split('T');

  return (
    <div className="datetime-field">
      <DatePicker
        id={id}
        value={date}
        min={min ? min.split('T')[0] : undefined}
        clearable
        onChange={(next) => onChange(next ? `${next}T${time || '00:00'}` : '')}
      />
      <input
        className="awj-input datetime-field__time"
        type="time"
        value={time}
        disabled={!date}
        aria-label={t('content.timeOfDay')}
        onChange={(event) => onChange(`${date}T${event.target.value || '00:00'}`)}
      />
    </div>
  );
}
